import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

function PendingApprovalPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenantStatus, setTenantStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const tenantId = location.state?.tenantId;
  const companyName = location.state?.companyName;

  useEffect(() => {
    if (tenantId) {
      checkTenantStatus();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const checkTenantStatus = async () => {
    try {
      setChecking(true);
      const response = await fetch(`/api/v1/onboarding/tenant/${tenantId}/status`);
      const data = await response.json();

      if (response.ok) {
        setTenantStatus(data.tenant);
      } else {
        toast.error('Failed to check status');
      }
    } catch (error) {
      console.error('Error checking tenant status:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  const handleBackToLanding = () => {
    navigate('/landing');
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckIcon />;
      case 'rejected':
        return <ScheduleIcon />;
      case 'pending':
        return <ScheduleIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'approved':
        return 'Your account has been approved! You can now sign in and start using the platform.';
      case 'rejected':
        return 'Your account application has been rejected. Please contact support for more information.';
      case 'pending':
        return 'Your account is currently under review. We will notify you once it has been approved.';
      default:
        return 'Unknown status';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToLanding}
              sx={{ position: 'absolute', left: 16, top: 16 }}
            >
              Back to Home
            </Button>

            <BusinessIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />

            <Typography variant="h4" fontWeight={700} gutterBottom>
              Account Status
            </Typography>

            {companyName && (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {companyName}
              </Typography>
            )}
          </Box>

          {/* Status Card */}
          {tenantStatus && (
            <Card sx={{ mb: 4, border: `2px solid ${theme.palette[getStatusColor(tenantStatus.status)].main}` }}>
              <CardContent>
                <Stack spacing={3} alignItems="center">
                  <Chip
                    icon={getStatusIcon(tenantStatus.status)}
                    label={tenantStatus.status.toUpperCase()}
                    color={getStatusColor(tenantStatus.status)}
                    size="large"
                    sx={{ fontSize: '1rem', fontWeight: 600 }}
                  />

                  <Typography variant="body1" textAlign="center" color="text.secondary">
                    {getStatusMessage(tenantStatus.status)}
                  </Typography>

                  {tenantStatus.rejectionReason && (
                    <Alert severity="error" sx={{ width: '100%' }}>
                      <Typography variant="body2" fontWeight={600}>
                        Reason for rejection:
                      </Typography>
                      <Typography variant="body2">
                        {tenantStatus.rejectionReason}
                      </Typography>
                    </Alert>
                  )}

                  {tenantStatus.approvedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Approved on:
                      {' '}
                      {new Date(tenantStatus.approvedAt).toLocaleDateString()}
                    </Typography>
                  )}

                  {tenantStatus.subscription && (
                    <Box sx={{
                      textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2, width: '100%',
                    }}
                    >
                      <Typography variant="h6" color="primary" gutterBottom>
                        Trial Period
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your
                        {' '}
                        {tenantStatus.subscription.plan}
                        {' '}
                        plan is active until
                        {' '}
                        {new Date(tenantStatus.subscription.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Stack spacing={2}>
            {tenantStatus?.status === 'pending' && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={checkTenantStatus}
                disabled={checking}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  },
                }}
              >
                {checking ? 'Checking...' : 'Check Status'}
              </Button>
            )}

            {tenantStatus?.status === 'approved' && (
              <Button
                variant="contained"
                size="large"
                onClick={handleSignIn}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                Sign In to Dashboard
              </Button>
            )}

            <Button
              variant="text"
              onClick={handleBackToLanding}
              sx={{
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                },
              }}
            >
              Back to Home
            </Button>
          </Stack>

          {/* Additional Information */}
          <Box sx={{
            mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2,
          }}
          >
            <Typography variant="h6" gutterBottom>
              What happens next?
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                • Our team will review your application within 24-48 hours
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • You'll receive an email notification once approved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Once approved, you can sign in and start using all features
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Need help? Contact us at support@baiglabs.com
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default PendingApprovalPage;
